import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { campaignsApi } from "../../api/campaigns";
import { CampaignStatusBadge } from "../../components/CampaignStatusBadge";
import { CampaignStatus } from "../../types";

export const ViewCampaigns: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: campaigns, isLoading } = useQuery({
    queryKey: ["campaigns"],
    queryFn: campaignsApi.listCampaigns,
  });

  const deleteMutation = useMutation({
    mutationFn: campaignsApi.deleteCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });

  const handleDelete = (e: React.MouseEvent, campaignId: number) => {
    e.stopPropagation();
    if (
      window.confirm(
        "Are you sure you want to delete this campaign? This action cannot be undone and will delete all associated residents and responses."
      )
    ) {
      deleteMutation.mutate(campaignId);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-primary">Naef</h1>
              <span className="ml-4 text-gray-600">All Campaigns</span>
            </div>
            <button onClick={() => navigate("/admin/dashboard")} className="btn btn-secondary">
              Back to Dashboard
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button onClick={() => navigate("/admin/create-campaign")} className="btn btn-primary">
            Create New Campaign
          </button>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-xl text-gray-600">Loading campaigns...</p>
          </div>
        ) : campaigns && campaigns.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaigns.map((campaign) => (
              <div
                key={campaign.id}
                className="card hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/admin/campaign/${campaign.id}`)}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-1">{campaign.building_address}</h3>
                    <p className="text-sm text-gray-600">ID: {campaign.building_id}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {campaign.is_ppe && (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                        PPE
                      </span>
                    )}
                    <CampaignStatusBadge status={campaign.status} />
                  </div>
                </div>

                <div className="border-t pt-4 mt-4">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Total Residents</p>
                      <p className="text-lg font-bold">{campaign.resident_count}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Created</p>
                      <p className="text-sm font-medium">{formatDate(campaign.created_at)}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-center p-2 bg-yellow-50 rounded">
                      <p className="text-xs text-gray-600 mb-1">Pending</p>
                      <p className="text-sm font-bold text-yellow-600">{campaign.pending_count}</p>
                    </div>
                    <div className="text-center p-2 bg-blue-50 rounded">
                      <p className="text-xs text-gray-600 mb-1">Saved</p>
                      <p className="text-sm font-bold text-blue-600">{campaign.saved_count}</p>
                    </div>
                    <div className="text-center p-2 bg-green-50 rounded">
                      <p className="text-xs text-gray-600 mb-1">Done</p>
                      <p className="text-sm font-bold text-green-600">{campaign.done_count}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/admin/campaign/${campaign.id}`);
                    }}
                    className="btn btn-primary flex-1 text-sm"
                  >
                    View Details
                  </button>
                  {campaign.status === CampaignStatus.DRAFT && (
                    <button
                      onClick={(e) => handleDelete(e, campaign.id)}
                      className="btn bg-red-600 hover:bg-red-700 text-white text-sm px-4"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card text-center py-12">
            <p className="text-xl text-gray-600 mb-4">No campaigns found</p>
            <button onClick={() => navigate("/admin/create-campaign")} className="btn btn-primary">
              Create Your First Campaign
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
